/**
 * Tooltip.
 *
 * Positioned with `position: fixed` against the trigger's bounding box and
 * rendered into a portal, so it escapes the `overflow: hidden` that every
 * scrolling panel in this app has. Flips when it would leave the viewport.
 *
 * Opens on hover **and** on focus, and closes on Escape — a terminal is
 * driven from the keyboard, and a tooltip that only exists for the mouse is
 * a tooltip half the users never see.
 */

import { AlertCircle, HelpCircle } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  /** Milliseconds before showing. 0 for controls that need instant feedback. */
  delay?: number;
  className?: string;
  /** Wrapper display. `block` when wrapping a full-width row. */
  as?: 'inline-flex' | 'block';
  disabled?: boolean;
  /**
   * How wide the bubble may get.
   *
   * `sm` (280px) is right for a sentence. `lg` (400px) exists for the field
   * explanations [F10], which are three labelled paragraphs and a table of
   * dropdown options — at 280px those wrap to a column of two or three words
   * and the worked example, the part the owner actually asked for, becomes
   * the hardest thing on screen to read.
   */
  width?: 'sm' | 'lg';
}

const GAP = 6;

const WIDTHS: Record<'sm' | 'lg', string> = {
  sm: 'max-w-[280px]',
  lg: 'max-w-[400px]',
};

export function Tooltip({
  content,
  children,
  side = 'top',
  delay = 250,
  className,
  as = 'inline-flex',
  disabled = false,
  width = 'sm',
}: TooltipProps) {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const show = useCallback(() => {
    if (disabled) return;
    clearTimer();
    if (delay <= 0) {
      setOpen(true);
      return;
    }
    timer.current = window.setTimeout(() => setOpen(true), delay);
  }, [clearTimer, delay, disabled]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') hide();
    };
    const onScroll = (): void => hide();
    window.addEventListener('keydown', onKey);
    // Capture phase: a tooltip anchored to a row inside a virtualised table
    // must close when that table scrolls, not just the window.
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current?.getBoundingClientRect();
    const tip = tooltipRef.current?.getBoundingClientRect();
    if (!trigger || !tip) return;

    const { innerWidth: vw, innerHeight: vh } = window;
    let resolved = side;

    // Flip rather than clip.
    if (side === 'top' && trigger.top - tip.height - GAP < 0) resolved = 'bottom';
    else if (side === 'bottom' && trigger.bottom + tip.height + GAP > vh) resolved = 'top';
    else if (side === 'left' && trigger.left - tip.width - GAP < 0) resolved = 'right';
    else if (side === 'right' && trigger.right + tip.width + GAP > vw) resolved = 'left';

    let top: number;
    let left: number;
    switch (resolved) {
      case 'bottom':
        top = trigger.bottom + GAP;
        left = trigger.left + trigger.width / 2 - tip.width / 2;
        break;
      case 'left':
        top = trigger.top + trigger.height / 2 - tip.height / 2;
        left = trigger.left - tip.width - GAP;
        break;
      case 'right':
        top = trigger.top + trigger.height / 2 - tip.height / 2;
        left = trigger.right + GAP;
        break;
      default:
        top = trigger.top - tip.height - GAP;
        left = trigger.left + trigger.width / 2 - tip.width / 2;
    }

    setPosition({
      top: Math.max(4, Math.min(top, vh - tip.height - 4)),
      left: Math.max(4, Math.min(left, vw - tip.width - 4)),
    });
  }, [open, side]);

  return (
    <>
      <span
        ref={triggerRef}
        className={cn(as === 'block' ? 'block' : 'inline-flex', className)}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </span>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            id={id}
            role="tooltip"
            style={{ top: position.top, left: position.left }}
            className={cn(
              'pointer-events-none fixed z-[100] rounded border border-border',
              WIDTHS[width],
              'bg-surface-3 px-2 py-1 text-[11px] leading-snug text-hi shadow-[var(--shadow-pop)]',
            )}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * A `?` that explains a metric.
 *
 * Use it liberally on anything derived. "Profit factor" and "R-multiple"
 * are not self-explanatory, and a number nobody understands is a number
 * that gets ignored — including the ones that were trying to warn you.
 */
export function InfoHint({
  children,
  side = 'top',
  className,
  width,
  tone = 'muted',
  label = 'What does this mean?',
}: {
  children: ReactNode;
  side?: TooltipSide;
  className?: string;
  width?: 'sm' | 'lg';
  /**
   * `warn` for a field whose explanation is *missing*. [F10]
   *
   * A grey `?` that opens onto "nobody wrote this yet" is a gap you have to
   * hover to find. An amber one is a gap you can see from across the form,
   * which is the difference between a mechanism that catches the next
   * undocumented parameter and one that only records it.
   */
  tone?: 'muted' | 'warn';
  label?: string;
}) {
  const Icon = tone === 'warn' ? AlertCircle : HelpCircle;
  return (
    <Tooltip content={children} side={side} delay={120} {...(width ? { width } : {})}>
      <button
        type="button"
        tabIndex={0}
        aria-label={label}
        data-help-tone={tone}
        /*
         * A `?` must never activate the thing it annotates.
         *
         * `Toggle` makes its label clickable, so the hint inside a toggle's
         * label would flip the switch on the way to explaining it — the
         * control changing state as a side effect of asking what it does.
         */
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        className={cn(
          'inline-flex items-center rounded transition-colors',
          tone === 'warn' ? 'text-warn hover:text-warn' : 'text-lo hover:text-mid',
          className,
        )}
      >
        <Icon size={12} aria-hidden />
      </button>
    </Tooltip>
  );
}
